import { isWeb, SizableTextProps, useMedia, useTheme } from '@mythical/ui'
import React from 'react'
import Svg, { Text as SvgText } from 'react-native-svg'

type ShadowOffset = {
  width: number
  height: number
}

type QuarterTextProps = Omit<
  SizableTextProps,
  'children' | 'color' | 'textShadowColor' | 'textShadowOffset' | 'textShadowRadius'
>

export type QuarterCodeTextProps = {
  quarter?: string | null
  textColor?: SizableTextProps['color']
  /** @deprecated Use textColor instead */
  color?: SizableTextProps['color']
  outlineColor?: SizableTextProps['color']
  strokeWidth?: number
  shadowColor?: string
  shadowOffset?: ShadowOffset
  shadowRadius?: number
  /** @deprecated No longer used — outline is now SVG stroke-based */
  outlineOffsets?: ReadonlyArray<readonly [number, number]>
  textProps?: QuarterTextProps
}

const DISPLAY_FONT_FAMILY = isWeb
  ? 'var(--font-obviously-wide)'
  : 'Obviously-WideBold'

// Maps Tamagui size tokens to pixel values (from tamagui.config.ts defaultSizes)
const SIZE_MAP: Record<string, number> = {
  '$8': 30,
  '$9': 36,
  '$9.5': 40,
  '$10': 48,
  '$10.5': 56,
  '$11': 60,
}

const DEFAULT_FONT_SIZE = 36 // $9

function resolveFontSize(
  textProps: QuarterTextProps | undefined,
  media: ReturnType<typeof useMedia>,
): number {
  if (textProps) {
    // Check responsive breakpoints (most specific first)
    if (media.gtSm && textProps.$gtSm?.size) {
      return SIZE_MAP[String(textProps.$gtSm.size)] ?? DEFAULT_FONT_SIZE
    }
    if (textProps.size) {
      return SIZE_MAP[String(textProps.size)] ?? DEFAULT_FONT_SIZE
    }
  }
  return DEFAULT_FONT_SIZE
}

function resolveThemeColor(
  token: SizableTextProps['color'] | undefined,
  theme: ReturnType<typeof useTheme>,
  fallback: string,
): string {
  if (!token) return fallback
  const tokenStr = String(token)
  if (tokenStr.startsWith('$')) {
    const key = tokenStr.slice(1)
    return (theme as Record<string, { val?: string }>)[key]?.val ?? fallback
  }
  return tokenStr
}

export function getQuarterCode(quarter?: string | null): string {
  if (!quarter) return ''
  const match = quarter.toUpperCase().match(/Q[1-4]/)
  return match?.[0] || quarter.toUpperCase()
}

export const QuarterCodeText = ({
  quarter,
  textColor,
  color,
  outlineColor = '$fgPrimary',
  strokeWidth = 6,
  shadowColor,
  shadowOffset = { width: 6, height: 6 },
  textProps,
}: QuarterCodeTextProps) => {
  const quarterCode = getQuarterCode(quarter)
  const theme = useTheme()
  const media = useMedia()

  const resolvedTextColor = resolveThemeColor(
    textColor ?? color ?? '$fgInverted',
    theme,
    '#ffffff',
  )
  const resolvedOutlineColor = resolveThemeColor(outlineColor, theme, '#000000')
  const resolvedShadowColor = shadowColor
    ? resolveThemeColor(shadowColor as SizableTextProps['color'], theme, resolvedOutlineColor)
    : resolvedOutlineColor

  const fontSize = resolveFontSize(textProps, media)

  if (!quarterCode) return null

  // Estimate SVG dimensions based on character count and font size.
  // The display font ("Obviously Wide Bold") is an extended-width typeface,
  // so characters are significantly wider than typical fonts.
  const padding = strokeWidth
  const estimatedCharWidth = fontSize * 1.05
  const svgWidth =
    estimatedCharWidth * quarterCode.length + padding * 2 + shadowOffset.width
  const svgHeight = fontSize + padding * 2 + shadowOffset.height

  // Right-align text so varying digit widths (1 vs 2 vs 3) don't
  // overflow the card edge. Anchor at (svgWidth - padding) for the
  // main text; shadow is offset from that anchor.
  const textX = svgWidth - padding
  const textY = fontSize + padding * 0.5

  return (
    <Svg
      width={svgWidth}
      height={svgHeight}
      style={{ overflow: 'visible' as const }}
    >
      {/* Shadow layer */}
      <SvgText
        x={textX + shadowOffset.width}
        y={textY + shadowOffset.height}
        fill={resolvedShadowColor}
        fontFamily={DISPLAY_FONT_FAMILY}
        fontSize={fontSize}
        fontWeight="700"
        textAnchor="end"
      >
        {quarterCode}
      </SvgText>

      {/* Outline (stroke) layer */}
      <SvgText
        x={textX}
        y={textY}
        fill={resolvedOutlineColor}
        stroke={resolvedOutlineColor}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
        strokeLinecap="round"
        fontFamily={DISPLAY_FONT_FAMILY}
        fontSize={fontSize}
        fontWeight="700"
        textAnchor="end"
      >
        {quarterCode}
      </SvgText>

      {/* Fill layer (main text color on top) */}
      <SvgText
        x={textX}
        y={textY}
        fill={resolvedTextColor}
        fontFamily={DISPLAY_FONT_FAMILY}
        fontSize={fontSize}
        fontWeight="700"
        textAnchor="end"
      >
        {quarterCode}
      </SvgText>
    </Svg>
  )
}
